import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SelectFormField, SelectOption } from "@/components/shared/Form/SelectFormField";
import { useForm, FormProvider } from "react-hook-form";
import { UserRole, PropertyResponseDto } from "@/common/api/datasources/Api";
import { propertiesService } from "@/common/api/services/properties";
import { api } from "@/common/api/instance";
import { User as UserIcon, UserCog as UserCogIcon, PenSquare as EditIcon, X as CancelIcon } from "lucide-react";
import { PageableResponse } from "@/common/api/interfaces/pagination";
import { Button } from "@/components/ui/button";

interface PropertyContactsProps {
    property: PropertyResponseDto;
}

export const PropertyContacts = ({ property }: PropertyContactsProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [isEditMode, setIsEditMode] = useState(false);
    const [isAssigningManager, setIsAssigningManager] = useState(false);
    const [isAssigningOwner, setIsAssigningOwner] = useState(false);
    const [previousManagerUUID, setPreviousManagerUUID] = useState(property?.managerUUID || "");
    const [previousOwnerUUID, setPreviousOwnerUUID] = useState(property?.ownerUUID || "");

    const managerForm = useForm({
        defaultValues: {
            managerUUID: property?.managerUUID || "",
        }
    });

    const ownerForm = useForm({
        defaultValues: {
            ownerUUID: property?.ownerUUID || "",
        }
    });

    const selectedManagerUUID = managerForm.watch("managerUUID");
    const selectedOwnerUUID = ownerForm.watch("ownerUUID");

    useEffect(() => {
        if (property) {
            managerForm.setValue("managerUUID", property.managerUUID || "");
            ownerForm.setValue("ownerUUID", property.ownerUUID || "");
            setPreviousManagerUUID(property.managerUUID || "");
            setPreviousOwnerUUID(property.ownerUUID || "");
        }
    }, [property]);

    useEffect(() => {
        if (isEditMode && selectedManagerUUID !== previousManagerUUID) {
            setPreviousManagerUUID(selectedManagerUUID);
            setIsAssigningManager(true);
            assignManager(selectedManagerUUID || null);
        }
    }, [selectedManagerUUID, isEditMode]);

    useEffect(() => {
        if (isEditMode && selectedOwnerUUID !== previousOwnerUUID) {
            setPreviousOwnerUUID(selectedOwnerUUID);
            setIsAssigningOwner(true);
            assignOwner(selectedOwnerUUID || null);
        }
    }, [selectedOwnerUUID, isEditMode]);

    const { mutate: assignManager } = useMutation({
        mutationFn: (managerUUID: string | null) =>
            managerUUID
                ? propertiesService.assignManager(property.propertyUUID, managerUUID)
                : propertiesService.removeManager(property.propertyUUID),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["properties", property.propertyUUID] });
            toast({
                title: t("properties.details.managerAssigned"),
                description: t("properties.details.managerAssignedDesc"),
            });
            setIsAssigningManager(false);
        },
        onError: () => {
            toast({
                variant: "destructive",
                title: t("properties.details.managerAssignFailed"),
                description: t("common.tryAgain"),
            });
            managerForm.setValue("managerUUID", property.managerUUID || "");
            setIsAssigningManager(false);
        },
    });

    const { mutate: assignOwner } = useMutation({
        mutationFn: (ownerUUID: string | null) =>
            ownerUUID
                ? propertiesService.assignOwner(property.propertyUUID, ownerUUID)
                : propertiesService.removeOwner(property.propertyUUID),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["properties", property.propertyUUID] });
            toast({
                title: t("properties.details.ownerAssigned"),
                description: t("properties.details.ownerAssignedDesc"),
            });
            setIsAssigningOwner(false);
        },
        onError: () => {
            toast({
                variant: "destructive",
                title: t("properties.details.ownerAssignFailed"),
                description: t("common.tryAgain"),
            });
            ownerForm.setValue("ownerUUID", property.ownerUUID || "");
            setIsAssigningOwner(false);
        },
    });

    const fetchManagers = async (context: {
        pageParam?: number;
        queryKey: string[];
    }): Promise<PageableResponse<SelectOption>> => {
        const search = context.queryKey[2] || "";

        try {
            const response = await api.users.usersControllerFindAll({
                page: context.pageParam || 0,
                size: 10,
                role: UserRole.STAFF,
                keyword: search,
            });

            const formattedManagers = response.data.content.map((user) => ({
                value: user.userUUID,
                label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
            }));

            return {
                content: formattedManagers,
                page: response.data.page || 0,
                size: response.data.size || 10,
                totalElements: response.data.totalElements || 0,
                totalPages: response.data.totalPages || 0,
                numberOfElements: response.data.numberOfElements || 0,
                last: response.data.last || false,
                first: (context.pageParam || 0) === 0
            };
        } catch (error) {
            console.error("Failed to fetch managers:", error);
            toast({
                variant: "destructive",
                title: t("properties.errors.loadManagersError"),
                description: t("common.errors.tryAgain"),
            });

            return {
                content: [],
                page: 0,
                size: 10,
                totalElements: 0,
                totalPages: 0,
                numberOfElements: 0,
                last: true,
                first: true
            };
        }
    };

    const fetchOwners = async (context: {
        pageParam?: number;
        queryKey: string[];
    }): Promise<PageableResponse<SelectOption>> => {
        const search = context.queryKey[2] || "";

        try {
            const response = await api.users.usersControllerFindAll({
                page: context.pageParam || 0,
                size: 10,
                role: UserRole.OWNER,
                keyword: search,
            });

            const formattedOwners = response.data.content.map((user) => ({
                value: user.userUUID,
                label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
            }));

            return {
                content: formattedOwners,
                page: response.data.page || 0,
                size: response.data.size || 10,
                totalElements: response.data.totalElements || 0,
                totalPages: response.data.totalPages || 0,
                numberOfElements: response.data.numberOfElements || 0,
                last: response.data.last || false,
                first: (context.pageParam || 0) === 0
            };
        } catch (error) {
            console.error("Failed to fetch owners:", error);
            toast({
                variant: "destructive",
                title: t("properties.errors.loadOwnersError"),
                description: t("common.errors.tryAgain"),
            });

            return {
                content: [],
                page: 0,
                size: 10,
                totalElements: 0,
                totalPages: 0,
                numberOfElements: 0,
                last: true,
                first: true
            };
        }
    };

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                    <UserIcon className="h-5 w-5 text-primary" />
                    {t("properties.details.contacts")}
                </CardTitle>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditMode(!isEditMode)}
                    className="h-8 px-2"
                >
                    {isEditMode ? (
                        <CancelIcon className="h-4 w-4" />
                    ) : (
                        <EditIcon className="h-4 w-4" />
                    )}
                </Button>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="space-y-4">
                    <FormProvider {...managerForm}>
                        <SelectFormField
                            name="managerUUID"
                            control={managerForm.control}
                            label={t("properties.details.manager")}
                            placeholder={t("properties.form.managerPlaceholder")}
                            disabled={!isEditMode || isAssigningManager}
                            queryKey={["users", "managers", ""]}
                            queryFn={fetchManagers}
                            clearable
                        />
                    </FormProvider>
                </div>

                <div className="space-y-4">
                    <FormProvider {...ownerForm}>
                        <SelectFormField
                            name="ownerUUID"
                            control={ownerForm.control}
                            label={t("properties.details.owner")}
                            placeholder={t("properties.form.ownerPlaceholder")}
                            disabled={!isEditMode || isAssigningOwner}
                            queryKey={["users", "owners", ""]}
                            queryFn={fetchOwners}
                            clearable
                        />
                    </FormProvider>
                </div>
            </CardContent>
        </Card>
    );
}; 